import type { MappingRule, SuggestionCandidate } from './suggestion-types';

/*
 * The reason a listing was matched, in plain words (design section 1). The
 * rule and reason come from the suggester; a person never sees those codes.
 */

const SIMPLE_RULES: Partial<Record<MappingRule, string>> = {
  exact_sku: 'Same SKU',
  exact_gtin: 'Same barcode (UPC/EAN)',
  exact_asin: 'Same ASIN',
};

const NO_PRODUCT = 'No product found';
const ENDING_AFTER_DASH = /-(\d{5})$/;

/** `pack_prefix: ITEM x2 via exact_sku; qty 147 = floor(295/2)` */
const PACK_REASON = /^pack_prefix: (.+?) x(\d+) via /;
const PACK_STOCK = /; qty (\d+) = floor\((\d+)\/(\d+)\)/;
const TITLE_SCORE = /score (\d+(?:\.\d+)?)/;
const AMBIGUOUS_COUNT = /; (\d+) items/;

function packText(reason: string) {
  const match = PACK_REASON.exec(reason);
  if (!match) {
    return 'Looks like a multi-pack of this product';
  }

  const [, itemSku, pack] = match;
  const stock = PACK_STOCK.exec(reason);
  const evidence = stock
    ? ` (stock ${stock[1]} = ${stock[2]} / ${stock[3]})`
    : '';

  return `${pack}*${itemSku} = a ${pack}-pack of ${itemSku}${evidence}`;
}

function titleText(reason: string) {
  const score = TITLE_SCORE.exec(reason)?.[1];

  return score
    ? `Similar title (${Math.round(Number(score) * 100)}%)`
    : 'Similar title';
}

/** Plain words for the no-match codes that need no detail from the reason. */
const NO_MATCH_WORDS: [prefix: string, text: string][] = [
  ['shares_item', 'Another listing on this source points to the same product'],
  ['pack_unknown', 'The barcode matches, but the pack size is unclear'],
  ['title_weak', 'Only a weak title match'],
  ['no_key', 'This listing has no SKU, barcode or title'],
];

/** No suggestion: the reason starts with the no-match code, then `; detail`. */
function noMatchText(reason: string, candidates?: SuggestionCandidate[]) {
  const code = reason.split(';')[0].trim();

  if (code.startsWith('ambiguous')) {
    const count = AMBIGUOUS_COUNT.exec(reason)?.[1];

    return count
      ? `${count} products could match`
      : 'Several products could match';
  }

  if (code.startsWith('conflict')) {
    return 'Two rules disagree';
  }

  if (code === 'human_unmapped') {
    const sku = candidates?.[0]?.sku;

    return sku ? `You removed the link to ${sku}` : 'You removed this link';
  }

  const known = NO_MATCH_WORDS.find(([prefix]) => code.startsWith(prefix));

  return known ? known[1] : NO_PRODUCT;
}

/**
 * @param listingSku the listing's own SKU, to name the ending a `sku_suffix5`
 * match removed ("-00270"); without it the words stay general.
 * @param candidates the listing's held candidates; names the SKU of a removed link.
 */
export function whyText(
  rule: MappingRule | string,
  reason: string,
  listingSku?: string | null,
  candidates?: SuggestionCandidate[],
): string {
  const simple = SIMPLE_RULES[rule as MappingRule];
  if (simple) {
    return simple;
  }

  const ending = listingSku ? ENDING_AFTER_DASH.exec(listingSku)?.[0] : null;
  const removed = `SKU matches after removing the ${ending ?? '5-digit'} ending`;

  switch (rule) {
    case 'sku_suffix5':
      return removed;
    case 'sku_suffix5+gtin':
      return `${removed} and the barcode matches`;
    case 'pack_prefix':
      return packText(reason);
    case 'title':
      return titleText(reason);
    default:
      return noMatchText(reason, candidates);
  }
}
